package game

import domain.cards.RunState
import domain.pieces.BagRandomizer
import domain.pieces.Gravinome
import domain.pieces.GravitarPieceController
import domain.player.GameAction
import domain.player.KeyBindingStore
import domain.score.CardScoringEngine
import domain.score.StoryRulebook
import engine.controller.ScoreEngine
import engine.controller.defaults.board.ExtendedBoardController
import engine.controller.defaults.gravity.DefaultLevelHandler
import engine.controller.defaults.scoring.DefaultRulebook
import engine.controller.defaults.scoring.ScoreTracker
import engine.model.*
import engine.util.Logger

// ── Preset modes ──────────────────────────────────────────────────────────────

enum class GameMode { SPRINT, TIME_ATTACK, CUSTOM }

// ── Builder DSL ───────────────────────────────────────────────────────────────

class GameConfig {
    var mode: GameMode = GameMode.CUSTOM
    var matchConfig: MatchConfig? = null
    var playerConfig: PlayerConfig = PlayerConfig()
    var scoringEngineFactory: (() -> ScoreEngine)? = null
    var pieceCollection: Collection<Piece> = Gravinome.tetrominoes
    var bagRandomizer: BagRandomizer = BagRandomizer(pieceCollection, matchConfig?.gameplay?.previewSize ?: 5)
    var boardRows: Int = 20
    var boardCols: Int = 10
    var boardBuffer: Int = 4
}

// ── GravitarGame ──────────────────────────────────────────────────────────────

/**
 * Universal game session.
 *
 * A single instance is kept alive for the duration of a story run.
 * Call [swapBag] to change the piece set in-place between rooms.
 */
class GravitarGame(
    val gameId: String,
    configure: GameConfig.() -> Unit = {},
) {
    val config = GameConfig().apply(configure)
    private val _scoringEngine = config.scoringEngineFactory?.invoke()
        ?: CardScoringEngine(gameId, RunState(), StoryRulebook())

    // ── Match config resolution ───────────────────────────────────────────────

    private var _matchConfig: MatchConfig = config.matchConfig ?: when (config.mode) {
        GameMode.SPRINT -> MatchConfig(
            gravity = GravityConfig(levelHandler = DefaultLevelHandler()),
            gameplay = GameplayConfig(spinDetection = SpinMode.PIECE_DEFINED),
            objective = ObjectiveConfig(goalType = GameGoal.LINES, goalValue = 40.0),
        )

        GameMode.TIME_ATTACK -> MatchConfig(
            gameplay = GameplayConfig(spinDetection = SpinMode.PIECE_DEFINED),
            objective = ObjectiveConfig(goalType = GameGoal.TIME, goalValue = 120.0),
        )

        GameMode.CUSTOM -> MatchConfig()
    }

    // ── Internal components ───────────────────────────────────────────────────

    private val boardController = ExtendedBoardController(
        config.boardRows, config.boardCols, config.boardBuffer, _matchConfig.garbage,
    )
    private val tracker = ScoreTracker(gameId)

    private var _bag: BagRandomizer = config.bagRandomizer
    private var _pieceCollection: Collection<Piece> = config.pieceCollection
    private var _pieceController = GravitarPieceController(
        boardController.board, _bag, config.playerConfig, _matchConfig, gameId,
    )

    private val context = KtrisContextBuilder(gameId)
        .gameSettings(_matchConfig)
        .playerSettings(config.playerConfig)
        .bagManager(_bag)
        .boardManager(boardController)
        .pieceController(_pieceController)
        .scoringEngine(_scoringEngine)
        .scoreTracker(tracker)
        .build()

    val engine: GravitarEngine = GravitarEngine(context)

    // ── Key maps ──────────────────────────────────────────────────────────────

    private var keyDownMap: Map<String, GameAction> = buildKeyMap()
    private var keyUpMap: Map<String, GameAction> = buildKeyMap()

    // ── Public API ────────────────────────────────────────────────────────────

    val board get() = boardController.board
    val points get() = tracker.totalPoints
    val isGameOver get() = engine.isGameOver
    val isGoalMet get() = engine.isGoalMet
    val boardManager get() = boardController
    val scoringEngine get() = _scoringEngine
    val pieceCollection: Collection<Piece> get() = _pieceCollection
    val pieceController get() = _pieceController
    val bag: BagRandomizer get() = _bag
    val isFrozen get() = engine.isFrozen
    val matchConfig get() = _matchConfig

    fun update(deltaMs: Double) = engine.update(deltaMs)
    fun getSnapshot(): GameSnapshot? = engine.gameStateSnapshot()

    fun onKeyDown(key: String, isFresh: Boolean) {
        when (keyDownMap[key]) {
            GameAction.MOVE_LEFT -> engine.onMovement(Movement.MOVE_LEFT)
            GameAction.MOVE_RIGHT -> engine.onMovement(Movement.MOVE_RIGHT)
            GameAction.SOFT_DROP -> engine.onDrop(Drop.SOFT_DROP)
            GameAction.HARD_DROP -> engine.onDrop(Drop.HARD_DROP)
            GameAction.ROTATE_CW -> engine.onRotation(Rotation.ROTATE_CW, isFresh)
            GameAction.ROTATE_CCW -> engine.onRotation(Rotation.ROTATE_CCW, isFresh)
            GameAction.ROTATE_180 -> engine.onRotation(Rotation.ROTATE_180, isFresh)
            GameAction.HOLD -> engine.onHold(isFresh)
            else -> Unit
        }
    }

    fun onKeyUp(key: String) {
        when (keyUpMap[key]) {
            GameAction.MOVE_LEFT -> engine.onMovementRelease(Movement.MOVE_LEFT)
            GameAction.MOVE_RIGHT -> engine.onMovementRelease(Movement.MOVE_RIGHT)
            GameAction.ROTATE_CW -> engine.onRotationRelease(Rotation.ROTATE_CW)
            GameAction.ROTATE_CCW -> engine.onRotationRelease(Rotation.ROTATE_CCW)
            GameAction.ROTATE_180 -> engine.onRotationRelease(Rotation.ROTATE_180)
            else -> Unit
        }
    }

    fun reset() {
        tracker.reset()
        engine.reset()
        keyDownMap = buildKeyMap()
        keyUpMap = buildKeyMap()
    }

    /**
     * Replaces the active bag and piece collection without rebuilding the engine.
     * Takes effect on the next piece spawn.
     */
    fun swapBag(previewSize: Int, newCollection: Collection<Piece>) {
        val newBag = BagRandomizer(newCollection, previewSize)
        this._matchConfig = this.matchConfig.copy(gameplay = this.matchConfig.gameplay.copy(previewSize = previewSize))
        _bag = newBag
        _pieceCollection = newCollection
        pieceController.swapBag(newBag)
        Logger.info { "match Config: ${this._matchConfig}" }
        engine.updateGameSettings(_matchConfig)
        pieceController.updateMatchConfig(this._matchConfig)
    }

    fun setHoldEnabled(enabled: Boolean) {
        this._matchConfig = this.matchConfig.copy(gameplay = this.matchConfig.gameplay.copy(isHoldEnabled = enabled))
        this.engine.updateGameSettings(this.matchConfig)
        pieceController.updateMatchConfig(this.matchConfig)
    }

    fun setLockDelay(ms: Double) {
        this._matchConfig = this.matchConfig.copy(gravity = this.matchConfig.gravity.copy(lockDelay = ms))
        this.engine.updateGameSettings(this.matchConfig)
        pieceController.updateMatchConfig(this.matchConfig)
    }

    // ── Private ───────────────────────────────────────────────────────────────

    private fun buildKeyMap(): Map<String, GameAction> {
        val bindings = KeyBindingStore.loadActive()
        return buildMap {
            bindings.forEach { (action, keys) ->
                keys.forEach { code -> put(code, action) }
            }
        }
    }
}
